import { Feature, MapBrowserEvent, Map as OlMap } from "ol";
import { useEffect, useRef } from "react";
import { useInspectMap } from "./hooks/useInspectMap";
import styles from "./InspectAoiMap.module.scss";
import { ValidItem } from "./types/inspect";
import TileLayer from "ol/layer/Tile";
import XYZ from "ol/source/XYZ";
import { GeoJSON } from "ol/format";
import { FeatureCollection } from "geojson";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import { Fill, Stroke, Style } from "ol/style";
import useCustomNavigate from "hooks/common/useCustomNavigate";

type Props = {
  validItem: ValidItem;
  aois: FeatureCollection;
};

export default function InspectAoiMap({ validItem, aois }: Props) {
  const mapRef = useRef<OlMap | null>(null);
  const { initOlMap } = useInspectMap();
  const { navigateToReplacePath } = useCustomNavigate();

  useEffect(() => {
    const olMap = initOlMap(mapRef.current);
    if (!olMap) return;

    const layer = new TileLayer({
      source: new XYZ({
        url: validItem.beforeUrl,
      }),
    });

    olMap.addLayer(layer);

    const format = new GeoJSON();
    const feature = format.readFeature(validItem.bbox, {
      dataProjection: "EPSG:4326",
      featureProjection: "EPSG:3857",
    }) as Feature;

    const extent = feature.getGeometry()!.getExtent();
    olMap.getView().fit(extent, {
      padding: [50, 50, 50, 50],
    });

    const aoiFeatures = format.readFeatures(aois, {
      dataProjection: "EPSG:4326",
      featureProjection: "EPSG:3857",
    });

    const aoisLayer = new VectorLayer({
      source: new VectorSource({
        features: aoiFeatures,
      }),
      style: new Style({
        fill: new Fill({
          color: "transparent",
        }),
        stroke: new Stroke({
          color: "#FF0000",
          width: 5,
        }),
      }),
    });

    olMap.addLayer(aoisLayer);
    olMap.on("singleclick", (event: MapBrowserEvent) => {
      handleClickFeatureEvent(event);
    });
  }, []);

  const handleClickFeatureEvent = (event: MapBrowserEvent) => {
    if (!event.map) return;
    const features = event.map?.getFeaturesAtPixel(event.pixel);
    if (features.length === 0) return;
    const feature = features[0];

    navigateToReplacePath(
      `/inspect/${validItem.id}/${validItem.eeRequestId}/${feature.get("id")}`
    );
  };

  return <div id="map" className={styles["map"]}></div>;
}
